// Chart color resolution (KOA-18066).
//
// Recharts writes colors into SVG presentation ATTRIBUTES (stroke=/fill= and
// gradient <stop> defs) where CSS `var(--token)` does not reliably resolve —
// a proven pitfall from the mcp-chat-server dashboard. So tokens are resolved
// to concrete values via getComputedStyle, which means a theme flip reaches a
// chart only if something re-renders it — and a flip on its own re-renders
// nothing, so the charts kept the previous theme's colours until the analytics
// tab happened to render again. useChartPalette() subscribes to the theme and
// re-resolves on every flip.
// HTML tooltips CAN keep using var() directly.
import { useMemo } from "react";
import useAppliedTheme from "../../../../../shared/hooks/useAppliedTheme";
import type { DesignerTheme } from "../../../../../shared/utility/designer-theme";

export interface ChartPalette {
  accent: string;
  grid: string;
  axisText: string;
  /** Categorical series colors for the model-mix donut (5 + "Other"). */
  series: string[];
}

const FALLBACK_PALETTE: ChartPalette = {
  accent: "#7c5cff",
  grid: "#e3e5ea",
  axisText: "#7a8499",
  series: ["#7c5cff", "#3b82f6", "#22c55e", "#eab308", "#f97316", "#7a8499"]
};

export function getChartPalette(): ChartPalette {
  if (typeof window === "undefined") return FALLBACK_PALETTE;
  // One computed style for all ten tokens rather than one per token.
  const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);
  const tokenOr = (token: string, fallback: string): string => {
    const resolved: string = rootStyle.getPropertyValue(token).trim();
    return resolved.length > 0 ? resolved : fallback;
  };
  return {
    accent: tokenOr("--purple-6", FALLBACK_PALETTE.accent),
    grid: tokenOr("--dark-3", FALLBACK_PALETTE.grid),
    axisText: tokenOr("--dark-7", FALLBACK_PALETTE.axisText),
    series: [
      tokenOr("--purple-6", FALLBACK_PALETTE.series[0]),
      tokenOr("--blue-5", FALLBACK_PALETTE.series[1]),
      tokenOr("--green-5", FALLBACK_PALETTE.series[2]),
      tokenOr("--yellow", FALLBACK_PALETTE.series[3]),
      tokenOr("--orange-5", FALLBACK_PALETTE.series[4]),
      tokenOr("--dark-6", FALLBACK_PALETTE.series[5])
    ]
  };
}

/** The chart palette for the painted theme, re-resolved whenever the theme flips. */
export function useChartPalette(): ChartPalette {
  const theme: DesignerTheme = useAppliedTheme();
  // `theme` is not read inside: it is the signal that the tokens getChartPalette reads have changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- theme stands in for the CSS tokens
  return useMemo((): ChartPalette => getChartPalette(), [theme]);
}
